<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref, watch } from 'vue'

import {
  STATUS_LABELS as CHARGE_STATUS,
  STATUS_TAG as CHARGE_TAG,
  listCharges,
  type ChargeRow,
  type ChargeStatus,
} from '../api/charges'
import { customerOption, listCustomerOptions, type Customer } from '../api/customers'
import {
  STATUS_LABELS,
  STATUS_TAG,
  confirmSettlement,
  createSettlement,
  deleteSettlement,
  exportSettlements,
  getSettlement,
  listSettlements,
  paySettlement,
  sumAmounts,
  type ListRow,
  type SettleStatus,
} from '../api/settlements'
import { csvName, saveBlob } from '../utils/download'

const rows = ref<ListRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const customers = ref<Customer[]>([])
const refError = ref<unknown>(null)
const exporting = ref(false)

// 参照下拉用 undefined 表示「未选」：0 会被 el-select 当成值显示成「0」，placeholder 就没了
const query = reactive({
  keyword: '',
  customer: undefined as number | undefined,
  period: '',
  status: '',
  page: 1,
  size: 20,
})

const formOpen = ref(false)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive({
  customer_id: undefined as number | undefined,
  period: '' as string,
  remark: '',
  charge_ids: [] as number[],
})

/** 勾选表：只列该客户还没挂任何结算单的待结算明细 */
const pending = ref<ChargeRow[]>([])
const pendingLoading = ref(false)

const detailOpen = ref(false)
const detailLoading = ref(false)
const detailError = ref<unknown>(null)
const detail = ref<{
  settlement: ListRow
  customer_name: string
  charges: ChargeRow[]
} | null>(null)

const detailTotal = computed(() => (detail.value ? sumAmounts(detail.value.charges) : 0))

const formErrorText = computed(() => {
  if (!form.customer_id) return '请选择客户'
  if (form.period && !/^\d{4}-\d{2}$/.test(form.period)) return '账期格式应为 YYYY-MM'
  return ''
})

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listSettlements({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      customer: query.customer,
      period: query.period || undefined,
      status: query.status || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

async function loadRefs(): Promise<void> {
  refError.value = null
  try {
    customers.value = await listCustomerOptions()
  } catch (e) {
    refError.value = e
  }
}

onMounted(() => {
  void load()
  void loadRefs()
})

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

async function loadPending(): Promise<void> {
  if (!form.customer_id) {
    pending.value = []
    return
  }
  pendingLoading.value = true
  try {
    const res = await listCharges({
      page: 1,
      size: 200,
      customer: form.customer_id,
      status: 'pending',
    })
    // 已经挂在别的结算单上的计费仍是 pending，只能挂 settle_id = 0 的
    pending.value = res.list.filter((row) => row.settle_id === 0)
    form.charge_ids = []
  } catch (e) {
    formError.value = e
    pending.value = []
  } finally {
    pendingLoading.value = false
  }
}

watch(
  () => form.customer_id,
  () => {
    if (formOpen.value) void loadPending()
  },
)

/** 勾选表的选中行 → 请求体的 charge_ids；空数组表示该客户全部 pending 计费 */
function onChargeSelect(sel: ChargeRow[]): void {
  form.charge_ids = sel.map((row) => row.id)
}

function openCreate(): void {
  Object.assign(form, {
    customer_id: customers.value[0]?.id,
    period: '',
    remark: '',
    charge_ids: [] as number[],
  })
  formError.value = null
  pending.value = []
  formOpen.value = true
  void loadPending()
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  try {
    await createSettlement({
      customer_id: form.customer_id ?? 0,
      period: form.period || undefined,
      charge_ids: form.charge_ids.length ? [...form.charge_ids] : undefined,
      remark: form.remark.trim() || undefined,
    })
    formOpen.value = false
    await load('结算单已创建')
  } catch (e) {
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function act(row: ListRow, action: 'confirm' | 'pay', text: string): Promise<void> {
  notice.value = ''
  error.value = null
  try {
    if (action === 'confirm') await confirmSettlement(row.id)
    else await paySettlement(row.id)
    await load(text)
  } catch (e) {
    error.value = e
  }
}

async function remove(row: ListRow): Promise<void> {
  notice.value = ''
  error.value = null
  try {
    await deleteSettlement(row.id)
    await load(`已删除结算单「${row.doc_no}」，挂载的计费明细已回到待结算`)
  } catch (e) {
    error.value = e
  }
}

async function openDetail(row: ListRow): Promise<void> {
  detailOpen.value = true
  detail.value = null
  detailError.value = null
  detailLoading.value = true
  try {
    const d = await getSettlement(row.id)
    detail.value = {
      settlement: { ...d.settlement, customer_name: d.customer_name, charge_count: d.charges.length },
      customer_name: d.customer_name,
      charges: d.charges,
    }
  } catch (e) {
    detailError.value = e
  } finally {
    detailLoading.value = false
  }
}

async function doExport(): Promise<void> {
  exporting.value = true
  error.value = null
  try {
    saveBlob(
      await exportSettlements({
        keyword: query.keyword.trim() || undefined,
        customer: query.customer,
        period: query.period || undefined,
        status: query.status || undefined,
      }),
      csvName('settlements'),
    )
    notice.value = 'CSV 已开始下载'
  } catch (e) {
    error.value = e
  } finally {
    exporting.value = false
  }
}
</script>

<template>
  <div class="yc-settle">
    <header class="yc-settle__bar">
      <el-input
        v-model="query.keyword"
        class="yc-settle__keyword"
        placeholder="结算单号"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select
        v-model="query.customer"
        class="yc-settle__ref"
        placeholder="全部客户"
        clearable
        filterable
        @change="search"
      >
        <el-option v-for="c in customers" :key="c.id" :label="customerOption(c)" :value="c.id" />
      </el-select>
      <el-date-picker
        v-model="query.period"
        class="yc-settle__period"
        type="month"
        value-format="YYYY-MM"
        placeholder="全部账期"
        clearable
        @change="search"
      />
      <el-select v-model="query.status" class="yc-settle__status" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'ops:settle:create'" type="primary" @click="openCreate">新建结算单</el-button>
      <el-button @click="load()">刷新</el-button>
      <el-button v-perm="'ops:report:export'" :loading="exporting" @click="doExport">导出 CSV</el-button>
      <span v-if="notice" class="yc-settle__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-settle__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <el-card v-loading="loading" class="yc-settle__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="doc_no" label="结算单号" width="170" />
        <el-table-column label="客户" min-width="150">
          <template #default="{ row }">{{ row.customer_name || `#${row.customer_id}` }}</template>
        </el-table-column>
        <el-table-column prop="period" label="账期" width="100" />
        <el-table-column prop="charge_count" label="笔数" width="80" align="right" />
        <el-table-column label="金额" width="120" align="right">
          <template #default="{ row }">{{ Number(row.total_amount).toFixed(2) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as SettleStatus]" size="small">
              {{ STATUS_LABELS[row.status as SettleStatus] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="付款时间" min-width="155">
          <template #default="{ row }">{{ row.paid_at ? formatDateTime(row.paid_at) : '—' }}</template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="130" show-overflow-tooltip>
          <template #default="{ row }">{{ row.remark || '—' }}</template>
        </el-table-column>
        <el-table-column prop="created_by" label="创建人" width="100" />
        <el-table-column label="操作" width="220" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openDetail(row)">详情</el-button>
            <el-button
              v-if="row.status === 'draft'"
              v-perm="'ops:settle:confirm'"
              link
              type="primary"
              @click="act(row, 'confirm', `结算单「${row.doc_no}」已确认`)"
            >
              确认
            </el-button>
            <el-button
              v-if="row.status === 'confirmed'"
              v-perm="'ops:settle:pay'"
              link
              type="success"
              @click="act(row, 'pay', `结算单「${row.doc_no}」已付款`)"
            >
              付款
            </el-button>
            <el-popconfirm
              v-if="row.status === 'draft'"
              title="确认删除该结算单？挂载的计费明细会回到待结算。"
              width="300"
              @confirm="remove(row)"
            >
              <template #reference>
                <el-button v-perm="'ops:settle:delete'" link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有结算单"
        description="把客户的待结算计费明细归到一张单，确认后付款。"
      >
        <el-button v-perm="'ops:settle:create'" type="primary" @click="openCreate">新建结算单</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-settle__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <!-- 新建 -->
    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      title="新建结算单"
      width="760px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-settle__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <div class="yc-settle__formrow">
          <el-form-item label="客户" :error="form.customer_id ? '' : '请选择客户'">
            <el-select v-model="form.customer_id" class="yc-settle__ref" filterable>
              <el-option v-for="c in customers" :key="c.id" :label="customerOption(c)" :value="c.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="账期">
            <el-date-picker v-model="form.period" type="month" value-format="YYYY-MM" placeholder="默认当月" clearable />
          </el-form-item>
          <el-form-item label="备注">
            <el-input v-model="form.remark" class="yc-settle__remark" maxlength="255" placeholder="可留空" />
          </el-form-item>
        </div>

        <el-form-item label="挂载的计费明细">
          <div class="yc-settle__charges" v-loading="pendingLoading">
            <el-table
              v-if="pending.length"
              :data="pending"
              size="small"
              max-height="300"
              @selection-change="onChargeSelect"
            >
              <el-table-column type="selection" width="44" />
              <el-table-column prop="charge_no" label="计费单号" width="170" />
              <el-table-column prop="order_no" label="订单号" width="170" />
              <el-table-column prop="rate_card_name" label="规则" min-width="120" show-overflow-tooltip />
              <el-table-column label="金额" width="100" align="right">
                <template #default="{ row }">{{ Number(row.amount).toFixed(2) }}</template>
              </el-table-column>
              <el-table-column label="创建时间" min-width="155">
                <template #default="{ row }">{{ formatDateTime(row.created_at) }}</template>
              </el-table-column>
            </el-table>
            <p v-else class="yc-settle__hint">该客户暂无未挂单的待结算明细。不勾选任何一行时，确认会把该客户全部待结算明细挂进来。</p>
          </div>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">
          创建
        </el-button>
        <span v-if="formErrorText" class="yc-settle__hint yc-settle__hint--inline">{{ formErrorText }}</span>
        <span v-if="form.charge_ids.length" class="yc-settle__hint yc-settle__hint--inline">
          已勾选 {{ form.charge_ids.length }} 笔
        </span>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>

    <!-- 详情 -->
    <el-dialog
      v-if="detailOpen"
      v-model="detailOpen"
      :title="detail ? `结算单详情 · ${detail.settlement.doc_no}` : '结算单详情'"
      width="860px"
    >
      <div v-if="detailError" class="yc-settle__error">
        <TraceIdAlert :error="detailError" />
      </div>

      <div v-loading="detailLoading">
        <template v-if="detail">
          <div class="yc-settle__facts">
            <div class="yc-settle__fact"><span>状态</span><el-tag :type="STATUS_TAG[detail.settlement.status]" size="small">{{ STATUS_LABELS[detail.settlement.status] }}</el-tag></div>
            <div class="yc-settle__fact"><span>客户</span>{{ detail.customer_name }}</div>
            <div class="yc-settle__fact"><span>账期</span>{{ detail.settlement.period }}</div>
            <div class="yc-settle__fact"><span>付款时间</span>{{ detail.settlement.paid_at ? formatDateTime(detail.settlement.paid_at) : '—' }}</div>
            <div class="yc-settle__fact"><span>备注</span>{{ detail.settlement.remark || '—' }}</div>
            <div class="yc-settle__fact"><span>创建人</span>{{ detail.settlement.created_by || '—' }}</div>
          </div>

          <h4 class="yc-settle__sect">计费明细</h4>
          <el-table v-if="detail.charges.length" :data="detail.charges" size="small">
            <el-table-column prop="charge_no" label="计费单号" width="170" />
            <el-table-column prop="order_no" label="订单号" width="170" />
            <el-table-column prop="rate_card_name" label="规则" min-width="120" show-overflow-tooltip />
            <el-table-column prop="qty" label="计费量" width="90" align="right" />
            <el-table-column label="金额" width="100" align="right">
              <template #default="{ row }">{{ Number(row.amount).toFixed(2) }}</template>
            </el-table-column>
            <el-table-column label="状态" width="96">
              <template #default="{ row }">
                <el-tag :type="CHARGE_TAG[row.status as ChargeStatus]" size="small">{{ CHARGE_STATUS[row.status as ChargeStatus] ?? row.status }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
          <p v-else class="yc-settle__hint">该结算单没有挂载计费明细。</p>

          <p class="yc-settle__sum">
            合计 <b>{{ detailTotal.toFixed(2) }}</b>
            （单据金额 {{ Number(detail.settlement.total_amount).toFixed(2) }}，共 {{ detail.charges.length }} 笔）
          </p>
        </template>
      </div>

      <template #footer>
        <el-button @click="detailOpen = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-settle {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 170px;
  }

  &__ref {
    width: 190px;
  }

  &__period {
    width: 150px;
  }

  &__status {
    width: 130px;
  }

  &__remark {
    width: 220px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__card {
    margin-bottom: var(--yc-space-4);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__formrow {
    display: flex;
    flex-wrap: wrap;
    gap: var(--yc-space-3);

    .el-form-item {
      margin-bottom: var(--yc-space-3);
    }
  }

  &__charges {
    width: 100%;
  }

  &__hint {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.6;

    &--inline {
      margin-left: var(--yc-space-3);
    }
  }

  &__sect {
    margin: var(--yc-space-5) 0 var(--yc-space-3);
    font-size: var(--yc-font);
    font-weight: 600;
  }

  &__facts {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--yc-space-2) var(--yc-space-4);
  }

  &__fact {
    display: flex;
    gap: var(--yc-space-2);
    font-size: var(--yc-font-sm);
    word-break: break-all;

    span {
      flex: none;
      color: var(--yc-text-secondary);
    }
  }

  &__sum {
    margin-top: var(--yc-space-3);
    text-align: right;
    font-size: var(--yc-font-sm);
  }
}
</style>
